import { useCallback, useState } from 'react';
import { Avatar } from '@klaro7/ui';

import { resolveUploadUrl } from '@/lib/uploads';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  useConversations,
  useMessages,
  useSendMessage,
} from '@/features/chat/hooks/useChat';
import { useChatSocket } from '@/features/chat/hooks/useChatSocket';
import { MessageThread } from '@/features/chat/components/MessageThread';
import { MessageInput } from '@/features/chat/components/MessageInput';
import { useCall } from '../context/CallContext';
import { ClientRunwayBadge, ClientRunwayBanner } from './ClientRunway';
import { CreditCountdown } from './CreditCountdown';
import { CallerTypeBadge } from './CallerTypeBadge';

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export function ChatSessionScreen() {
  const {
    callState,
    activeCallType,
    callInfo,
    elapsed,
    billingTick,
    clientRunway,
    endCall,
    pauseSession,
    promoEnding,
    peerDisconnected,
  } = useCall();
  const { user } = useAuth();
  const isProfessional = user?.role === 'professional';

  const professionalId = callInfo?.professionalId;
  const callerUserId = callInfo?.callerUserId;

  // Reuse the same chat data hooks ChatPage uses. The conversation for the
  // active session is resolved from whichever counterpart id callInfo carries:
  // the CALLER (user) side has professionalId, the CALLEE (professional) side
  // has callerUserId. Match a conversation on either, mirroring ChatPage's
  // ?professional=<id> find-or-create logic.
  const { data: conversations = [] } = useConversations();
  const conversation = conversations.find(
    (c) =>
      (professionalId != null &&
        String(c.professionalId) === String(professionalId)) ||
      (callerUserId != null && String(c.userId) === String(callerUserId))
  );
  // Prefer the conversation id threaded through the call payload (pre-created at
  // session initiate). It is present for BOTH parties, so the professional —
  // who has no professionalId and may not yet have the conversation in their
  // list — can still open the thread and write first. Fall back to the
  // resolved-from-list id for older sessions without a threaded id.
  const conversationId = callInfo?.conversationId ?? conversation?.id;

  const { data: messagesData, isLoading: msgsLoading } = useMessages(conversationId);
  const sendMessage = useSendMessage();
  const { isTyping, emitTypingStart, emitTypingStop } = useChatSocket(conversationId);

  const [sendError, setSendError] = useState<string | null>(null);

  const handleSend = useCallback(
    (content: string) => {
      setSendError(null);
      // Send by conversationId when it exists, otherwise start a new
      // conversation with the professional — same payload shape as ChatPage.
      const payload = conversationId
        ? { conversationId, content }
        : professionalId
          ? { professionalId: String(professionalId), content }
          : null;

      if (!payload) return;

      sendMessage.mutate(payload, {
        onError: (error: unknown) => {
          const msg = error instanceof Error ? error.message : 'Errore nell\'invio del messaggio';
          const apiError = (error as { response?: { data?: { error?: { message?: string } } } })
            ?.response?.data?.error?.message;
          setSendError(apiError || msg);
        },
      });
    },
    [conversationId, professionalId, sendMessage]
  );

  const handleTyping = useCallback(() => {
    if (conversationId) emitTypingStart(conversationId);
  }, [conversationId, emitTypingStart]);

  const handleTypingStop = useCallback(() => {
    if (conversationId) emitTypingStop(conversationId);
  }, [conversationId, emitTypingStop]);

  // Only render for an active paid CHAT session. CallOverlay also gates this,
  // but guard internally so the component is safe to mount anywhere.
  if (callState !== 'active' || activeCallType !== 'chat') return null;

  const messages = messagesData?.data ?? [];
  const typingActive = isTyping(conversationId ?? '');

  const otherName = callInfo?.remoteName || 'Consulente';
  const otherAvatar = resolveUploadUrl(callInfo?.remoteAvatar);

  const participants = {
    myName: 'Tu',
    otherName,
    otherAvatar,
    otherInitial: otherName[0]?.toUpperCase() ?? '?',
  };

  return (
    <div className="fixed inset-0 z-[90] flex flex-col bg-gradient-to-b from-brand-navy to-[#0c0b24]">
      {/* Billing header */}
      <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3 sm:px-6">
        <Avatar src={otherAvatar} fallback={otherName} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-white">{otherName}</p>
            {isProfessional && callInfo?.callerType && (
              <CallerTypeBadge callerType={callInfo.callerType} variant="dark" />
            )}
          </div>
          <p className="text-[11px] text-brand-cyan">Chat in corso</p>
        </div>

        {/* Live billing readout — reuses CallContext billing fields */}
        <div className="flex flex-col items-end gap-0.5 text-right">
          <span className="flex items-center gap-1.5 font-mono text-sm text-white/90">
            <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            {formatTime(elapsed)}
          </span>
          {/* Cliente: costo + countdown credito sempre in vista, lampeggia con
              CTA "Ricarica" a ≤3 min. billing-tick è emesso solo al cliente. */}
          {billingTick && (
            <span className="flex items-center gap-2 text-[11px] text-white/70">
              <span>{billingTick.currentCost.toFixed(2)} €</span>
              <CreditCountdown remainingMinutes={billingTick.remainingMinutes} compact />
            </span>
          )}
          {isProfessional && clientRunway && (
            <span className="text-[11px]">
              <ClientRunwayBadge remainingMinutes={clientRunway.remainingMinutes} />
            </span>
          )}
        </div>

        {/* Pausa manuale: solo consulente, sospende la fatturazione fino a 5
            minuti (poi la sessione termina da sola). */}
        {isProfessional && (
          <button
            onClick={pauseSession}
            className="ml-2 flex shrink-0 items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.12] px-3 py-2 text-sm font-medium text-white/90 backdrop-blur-sm transition-colors hover:bg-white/20"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" />
            </svg>
            Pausa
          </button>
        )}

        {/* End chat — same action CallScreen uses */}
        <button
          onClick={endCall}
          className="ml-2 shrink-0 rounded-full bg-gradient-to-b from-red-500 to-red-600 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:from-red-400 hover:to-red-500 active:scale-95"
        >
          Termina chat
        </button>
      </div>

      {promoEnding && (
        <div className="animate-pulse rounded-md bg-amber-500/90 px-2.5 py-1.5 text-[11px] font-semibold text-white">
          ⚠️ Tariffa agevolata 0,99 €/min in esaurimento (~{promoEnding.promoMinutesLeft} min). Poi tariffa consulente.
        </div>
      )}

      {isProfessional && clientRunway && (
        <ClientRunwayBanner remainingMinutes={clientRunway.remainingMinutes} />
      )}

      {/* Peer disconnesso: la sessione resta viva lato server in attesa che
          rientri, il billing continua — segnaliamolo senza interrompere la UI. */}
      {peerDisconnected && (
        <div className="bg-amber-50 px-4 py-2 text-center text-xs font-medium text-amber-700">
          L'altro partecipante si è disconnesso, attendi la riconnessione…
        </div>
      )}

      {/* Message thread + composer */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
        <MessageThread
          messages={messages}
          isLoading={msgsLoading}
          isTyping={typingActive}
          conversationId={conversationId ?? (professionalId ? '__new__' : undefined)}
          participants={participants}
          customerUserId={conversation ? String(conversation.userId) : callerUserId ? String(callerUserId) : undefined}
        />

        {sendError && (
          <div className="border-t border-red-100 bg-red-50 px-4 py-2 text-xs text-red-600">
            {sendError}
          </div>
        )}

        <MessageInput
          onSend={handleSend}
          onTyping={handleTyping}
          onTypingStop={handleTypingStop}
          disabled={!conversationId && !professionalId}
          bypassLimit
        />
      </div>
    </div>
  );
}
